import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { groupRows, newRowKey, containsVariable } from './promptText';
import LexicalRowInput from './LexicalRowInput';
import './llmPrompts.css';

/**
 * Edits a draft list of prompt rows ({ key, item_id?, heading, content }).
 * Rows are grouped by consecutive heading; moving a row past its group edge
 * moves it into the neighbouring group.
 *
 * resetToken: bump it whenever `rows` is replaced from outside (load / discard) —
 * the Lexical inputs are uncontrolled and only read their value on mount.
 */
// onCopyGroup(group): copy one heading section; clipboard + onPaste: paste a copied section.
export default function PromptRowEditor({ rows, onChange, readOnly, requiredVariables, globals, excluded, onExcludedChange, resetToken = 0, onCopyGroup, clipboard, onPaste }) {
  const set = (next) => onChange(next);

  const updateRow = (index, patch) => set(rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const renameGroup = (indexes, heading) => {
    const idx = new Set(indexes);
    set(rows.map((r, i) => (idx.has(i) ? { ...r, heading } : r)));
  };

  const moveRow = (index, dir) => {
    const next = [...rows];
    const row = next[index];
    const neighbour = next[index + dir];
    if (!neighbour) return;
    if (neighbour.heading !== row.heading) {
      next[index] = { ...row, heading: neighbour.heading };
    } else {
      next[index] = neighbour;
      next[index + dir] = row;
    }
    set(next);
  };

  const moveGroup = (groups, g, dir) => {
    if (!groups[g + dir]) return;
    const order = groups.map((_, i) => i);
    [order[g], order[g + dir]] = [order[g + dir], order[g]];
    set(order.flatMap(i => groups[i].rows.map(x => x.row)));
  };

  const removeRow = (index) => set(rows.filter((_, i) => i !== index));

  // Removes a heading with all its rows (the draft; saved with "Tallenna uusi versio").
  const [confirmGroupKey, setConfirmGroupKey] = useState(null);
  const removeGroup = (indexes) => {
    const drop = new Set(indexes);
    set(rows.filter((_, i) => !drop.has(i)));
    setConfirmGroupKey(null);
  };

  const addRowAfter = (index, heading) => {
    const next = [...rows];
    next.splice(index + 1, 0, { key: newRowKey(), item_id: null, heading, content: '' });
    set(next);
  };

  const addGroup = () => set([...rows, { key: newRowKey(), item_id: null, heading: 'Uusi otsikko', content: '' }]);

  const groups = groupRows(rows);
  const excludedSet = new Set(excluded || []);

  return (
    <div className="d-flex flex-column gap-3">
      <div className="llmp-sheet">
        {groups.map((group, g) => {
          const indexes = group.rows.map(x => x.index);
          const lastIndex = indexes[indexes.length - 1];
          return (
            <section key={group.rows[0].row.key} className="llmp-group">
              <div className="llmp-group-head">
                <input
                  className="llmp-heading"
                  value={group.heading}
                  readOnly={readOnly}
                  onChange={(e) => renameGroup(indexes, e.target.value)}
                  aria-label="Otsikko"
                />
                {onCopyGroup && (
                  <button
                    type="button"
                    className="llmp-icon llmp-copy"
                    title="Kopioi otsikko riveineen (liitettäväksi toiseen promptiin)"
                    onClick={() => onCopyGroup({ heading: group.heading, rows: group.rows.map(x => x.row) })}
                  >
                    <i className="bi bi-files" />
                  </button>
                )}
                {!readOnly && (
                  <div className="llmp-actions">
                    <button type="button" className="llmp-icon" title="Siirrä otsikko ylös" disabled={g === 0} onClick={() => moveGroup(groups, g, -1)}>
                      <i className="bi bi-chevron-up" />
                    </button>
                    <button type="button" className="llmp-icon" title="Siirrä otsikko alas" disabled={g === groups.length - 1} onClick={() => moveGroup(groups, g, 1)}>
                      <i className="bi bi-chevron-down" />
                    </button>
                    {(() => {
                      const hasLocked = group.rows.some(x => containsVariable(x.row.content, requiredVariables));
                      const onlyGroup = groups.length === 1;
                      return (
                        <button
                          type="button"
                          className="llmp-icon danger"
                          title={hasLocked
                            ? 'Otsikon alla on pakollinen muuttuja, sitä ei voi poistaa'
                            : onlyGroup ? 'Promptissa pitää olla vähintään yksi otsikko' : 'Poista otsikko ja sen rivit'}
                          disabled={hasLocked || onlyGroup}
                          onClick={() => setConfirmGroupKey(group.rows[0].row.key)}
                        >
                          <i className={`bi ${hasLocked ? 'bi-lock' : 'bi-trash3'}`} />
                        </button>
                      );
                    })()}
                  </div>
                )}
              </div>
              {confirmGroupKey === group.rows[0].row.key && (
                <div className="llmp-confirm">
                  <span>Poista otsikko <strong>{group.heading}</strong> ja sen {group.rows.length} riviä?</span>
                  <button type="button" className="btn btn-sm btn-danger py-0" onClick={() => removeGroup(indexes)}>Poista</button>
                  <button type="button" className="btn btn-sm btn-link py-0" onClick={() => setConfirmGroupKey(null)}>Peru</button>
                </div>
              )}

              {group.rows.map(({ row, index }) => {
                const locked = containsVariable(row.content, requiredVariables);
                return (
                  <div key={row.key} className="llmp-row">
                    <span className="llmp-bullet" />
                    <div className="llmp-row-body">
                      {readOnly ? (
                        <div className="llmp-readonly">{row.content}</div>
                      ) : (
                        <LexicalRowInput
                          key={`${row.key}-${resetToken}`}
                          value={row.content}
                          placeholder="Kirjoita ohje…"
                          onChange={(content) => updateRow(index, { content })}
                        />
                      )}
                    </div>
                    {!readOnly && (
                      <div className="llmp-actions">
                        <button type="button" className="llmp-icon" title="Siirrä ylös" disabled={index === 0} onClick={() => moveRow(index, -1)}>
                          <i className="bi bi-arrow-up" />
                        </button>
                        <button type="button" className="llmp-icon" title="Siirrä alas" disabled={index === rows.length - 1} onClick={() => moveRow(index, 1)}>
                          <i className="bi bi-arrow-down" />
                        </button>
                        <button
                          type="button"
                          className="llmp-icon danger"
                          title={locked ? 'Rivillä on pakollinen muuttuja, sitä ei voi poistaa' : 'Poista rivi'}
                          disabled={locked || rows.length === 1}
                          onClick={() => removeRow(index)}
                        >
                          <i className={`bi ${locked ? 'bi-lock' : 'bi-trash3'}`} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}

              {!readOnly && (
                <button type="button" className="llmp-add" onClick={() => addRowAfter(lastIndex, group.heading)}>
                  <i className="bi bi-plus-lg me-1" />Lisää rivi
                </button>
              )}
            </section>
          );
        })}
      </div>

      {!readOnly && (
        <div className="d-flex gap-2 flex-wrap">
          <button type="button" className="llmp-add-group" onClick={addGroup}>
            <i className="bi bi-plus-lg me-1" />Uusi otsikko
          </button>
          {clipboard && onPaste && (
            <button type="button" className="llmp-add-group llmp-paste" onClick={onPaste} title={`Kopioitu promptista: ${clipboard.from}`}>
              <i className="bi bi-clipboard-plus me-1" />Liitä: {clipboard.heading} ({clipboard.rows.length} riviä)
            </button>
          )}
        </div>
      )}

      {globals && globals.rows?.length > 0 && (
        <div className="llmp-sheet px-3 pt-2">
          <div className="d-flex align-items-baseline gap-2 flex-wrap mb-1">
            <span className="llmp-card-title">Yleiset säännöt</span>
            <span className="text-muted" style={{ fontSize: '0.78rem' }}>
              Koskevat kaikkia prompteja. Kytke pois, jos sääntö ei sovi tähän promptiin.
            </span>
          </div>
          {globals.rows.map((g) => {
            const on = !excludedSet.has(g.item_id);
            return (
              <div key={g.item_id} className={`llmp-global ${on ? '' : 'off'}`}>
                <Form.Check
                  type="switch"
                  id={`global-${g.item_id}`}
                  disabled={readOnly}
                  checked={on}
                  aria-label={g.content}
                  onChange={(e) => {
                    const next = new Set(excludedSet);
                    if (e.target.checked) next.delete(g.item_id); else next.add(g.item_id);
                    onExcludedChange([...next]);
                  }}
                />
                <label htmlFor={`global-${g.item_id}`} className="mb-0" style={{ cursor: readOnly ? 'default' : 'pointer' }}>
                  <div className="llmp-global-head">{g.heading}</div>
                  <div className="llmp-global-text" style={{ whiteSpace: 'pre-wrap' }}>{g.content}</div>
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
